import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchAllSubjects,
  fetchModulesBySubject,
  fetchTopicsByModule,
  fetchSectionsByTopic,
} from "../pages/services/noteHierarchyService";
import HierarchyDropdown from "./formComponents/HierarchyDropdown";
import MultiSelectDropdown from "./formComponents/MultiSelectDropdown";
import AlternateAnswersField from "./formComponents/AlternateAnswersField";
import TestCasesField, { cleanTestCases } from "./formComponents/TestCasesField";
import StarterCodeField from "./formComponents/StarterCodeField";
import { fetchLabels, createLabel } from "../pages/services/questionService";
import UpdateOptionsTable from "./UpdateOptionsTable";
import RichTextEditor from "./ReusableComponent/RichTextEditor";
import ImageUploadField from "./ReusableComponent/ImageUploadField";
import { isHtmlEmpty, useHighlightCode } from "./utils/utility";
import "../styles/AddForm.css";
import "../styles/AddQuestions.css";
import "../styles/QuestionForm.css";
import "../styles/UpdateQuestion.css";

const CODE_QUESTION_TYPE = "Code to compile with output";
const DIFFICULTY = ["Easy", "Medium", "Hard", "Very Hard", "Professional"];
const QUESTION_TYPES = [
  "Text",
  "Multiple Choice single selection",
  "Multiple Choice multi selection",
  "Descriptive",
  "Code to compile with output",
];

const API_URL = process.env.REACT_APP_API_URL;

// Prefix /uploads/ image srcs so the browser loads them from the backend
const prefixUploads = (html) =>
  html ? html.replace(/src="\/uploads\//g, `src="${API_URL}/uploads/`) : "";

// Strip the prefix back before saving to DB
const stripUploads = (html) =>
  html ? html.split(`src="${API_URL}/uploads/`).join(`src="/uploads/`) : "";

const Field = ({ label, required, error, children }) => (
  <div className="af-field">
    <label className="af-label">
      {label}
      {required && <span className="af-required">*</span>}
    </label>
    {children}
    {error && <span className="af-error">{error}</span>}
  </div>
);

const errorBorder = { border: "1.5px solid #ef4444", borderRadius: "8px" };

// Also embedded in the question preview modal on the list (embedded): there
// it takes the id as a prop, has no page header, and reports back instead of
// navigating — onSaved after a save, onCancel, onDeleted.
const UpdateQuestion = ({ questionId: questionIdProp, embedded = false, onSaved, onCancel, onDeleted } = {}) => {
  const { id: routeId } = useParams();
  const questionId = questionIdProp || routeId;
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    subject: "", module: "", section: "", topic: "",
    difficultyLevel: "", questionType: "",
    question: "", questionAttachment: "",
    answer: "", answerAttachment: "", answerExplanation: "",
    notes: "", questionLink: "", markPositive: "", markNegative: "",
    labels: [],
    alternateAnswers: [],
    rejectedAnswers: [],
    testCases: [],
    starterCode: "",
  });

  const [subjects, setSubjects] = useState([]);
  const [modules, setModules] = useState([]);
  const [topics, setTopics] = useState([]);
  const [sections, setSections] = useState([]);
  const [availableLabels, setAvailableLabels] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);

  const isMCQ =
    formData.questionType === "Multiple Choice single selection" ||
    formData.questionType === "Multiple Choice multi selection";

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showOptionDialog, setShowOptionDialog] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [errors, setErrors] = useState({});
  const [correctIds, setCorrectIds] = useState([]);
  const [loadedOptions, setLoadedOptions] = useState([]);
  const [dupError, setDupError] = useState("");
  const [dupChecking, setDupChecking] = useState(false);
  const pageRef = useRef(null);
  useHighlightCode(pageRef, [loadedOptions]);

  // Load subjects once, then load question and cascade
  useEffect(() => {
    const init = async () => {
      try {
        const subs = await fetchAllSubjects();
        setSubjects(subs);
        fetchLabels().then(setAvailableLabels).catch(() => setAvailableLabels([]));

        const res = await fetch(`${API_URL}/api/questions/${questionId}`);
        const response = await res.json();
        if (!res.ok || !response?.data) {
          if (embedded) setErrorMessage("This question could not be loaded.");
          else navigate("/not-found", { replace: true });
          return;
        }

        const q = response.data;
        const subjectId = q.subjectId ? q.subjectId.toString() : "";
        const moduleId = q.moduleId ? q.moduleId.toString() : "";
        const topicId = q.topicId ? q.topicId.toString() : "";

        setFormData({
          question: prefixUploads(q.question || ""),
          questionAttachment: q.questionAttachment || "",
          answer: prefixUploads(q.answer || ""),
          answerAttachment: q.answerAttachment || "",
          answerExplanation: prefixUploads(q.answerExplanation || ""),
          notes: q.notes || "",
          questionLink: q.questionLink || "",
          markPositive: q.markPositive ?? "",
          markNegative: q.markNegative ?? "",
          topic: topicId,
          section: q.section || "",
          module: moduleId,
          subject: subjectId,
          difficultyLevel: q.difficultyLevel || "",
          questionType: q.questionType || "",
          labels: q.labels || [],
          alternateAnswers: q.alternateAnswers || [],
          rejectedAnswers: q.rejectedAnswers || [],
          testCases: q.testCases || [],
          starterCode: q.starterCode || "",
        });

        const isMCQType =
          q.questionType === "Multiple Choice single selection" ||
          q.questionType === "Multiple Choice multi selection";
        if (isMCQType && q.answer) {
          setCorrectIds(q.answer.split(",").map((s) => s.trim()).filter(Boolean));
        }

        // Cascade: load modules for stored subject
        if (subjectId) {
          setLoadingModules(true);
          try {
            const mods = await fetchModulesBySubject(subjectId);
            setModules(mods);
          } finally {
            setLoadingModules(false);
          }
        }

        // Cascade: load topics for stored module
        if (moduleId) {
          setLoadingTopics(true);
          try {
            const tops = await fetchTopicsByModule(moduleId);
            setTopics(tops);
          } finally {
            setLoadingTopics(false);
          }
        }

        // Cascade: load distinct sections for stored topic
        if (topicId) {
          setLoadingSections(true);
          try {
            const secs = await fetchSectionsByTopic(topicId);
            setSections(secs);
          } finally {
            setLoadingSections(false);
          }
        }
      } catch (err) {
        console.error("Error loading question:", err);
      }
    };
    init();
  }, [questionId, navigate]);

  const clearError = (name) => {
    setErrors((p) => {
      const n = { ...p };
      delete n[name];
      if (Object.keys(n).length === 0) setErrorMessage("");
      return n;
    });
  };

  const handleCreateLabel = async (name) => {
    const created = await createLabel(name);
    setAvailableLabels((prev) => (prev.some((l) => l.value === created.value) ? prev : [...prev, created]));
    return created;
  };

  const checkDuplicate = async (html) => {
    const text = (html || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/&[a-zA-Z0-9#]+;/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!text) { setDupError(""); return; }
    setDupChecking(true);
    // Include current options so same question with different options isn't flagged
    const optionTexts = loadedOptions.map(o => (o.option || "").trim()).filter(Boolean);
    try {
      const res = await fetch(`${API_URL}/api/questions/check-duplicate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          questionHtml: html,
          optionTexts,
          answerHtml:   formData.answer           || "",
          solutionHtml: formData.answerExplanation || "",
          excludeId:    questionId,
          topicId:      formData.topic             || undefined,
        }),
      });
      const data = await res.json();
      if (data.isDuplicate) {
        const hint = data.subject ? ` — already exists under "${data.subject}"` : "";
        setDupError(`This question already exists in the system${hint}.`);
      } else {
        setDupError("");
      }
    } catch {
      setDupError("");
    } finally {
      setDupChecking(false);
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    clearError(name);

    if (name === "questionType") {
      const becomingMCQ =
        value === "Multiple Choice single selection" ||
        value === "Multiple Choice multi selection";

      if (!becomingMCQ) {
        // Switching away from MCQ: clear MCQ state, keep answer field empty so RTE starts blank
        setCorrectIds([]);
        setLoadedOptions([]);
        setFormData((p) => ({ ...p, questionType: value, answer: "" }));
        setShowOptionDialog(false);
        try {
          const optRes = await fetch(`${API_URL}/api/options/${questionId}`);
          if (!optRes.ok) throw new Error("Failed to fetch options");
          const optData = await optRes.json();
          for (const opt of optData.options || []) {
            await fetch(`${API_URL}/api/options/${opt._id}`, { method: "DELETE" });
          }
        } catch (err) {
          console.error("Error deleting options:", err);
        }
      } else {
        // Switching TO MCQ: clear any non-MCQ text answer so it doesn't bleed into correctIds logic
        setFormData((p) => ({ ...p, questionType: value, answer: "" }));
        setCorrectIds([]);
      }
    }
  };

  const handleSubjectChange = async (subjectId) => {
    setFormData((p) => ({ ...p, subject: subjectId, module: "", topic: "", section: "" }));
    setModules([]);
    setTopics([]);
    setSections([]);
    clearError("subject");
    if (subjectId) {
      setLoadingModules(true);
      try {
        const mods = await fetchModulesBySubject(subjectId);
        setModules(mods);
      } finally {
        setLoadingModules(false);
      }
    }
  };

  const handleModuleChange = async (moduleId) => {
    setFormData((p) => ({ ...p, module: moduleId, topic: "", section: "" }));
    setTopics([]);
    setSections([]);
    clearError("module");
    if (moduleId) {
      setLoadingTopics(true);
      try {
        const tops = await fetchTopicsByModule(moduleId);
        setTopics(tops);
      } finally {
        setLoadingTopics(false);
      }
    }
  };

  const handleTopicChange = async (topicId) => {
    setFormData((p) => ({ ...p, topic: topicId, section: "" }));
    setSections([]);
    clearError("topic");
    if (topicId) {
      setLoadingSections(true);
      try {
        const secs = await fetchSectionsByTopic(topicId);
        setSections(secs);
      } finally {
        setLoadingSections(false);
      }
    }
  };

  const handleSectionChange = (value) => {
    setFormData((p) => ({ ...p, section: value }));
    clearError("section");
  };

  const validate = () => {
    const e = {};
    if (!formData.subject)              e.subject        = "Subject is required";
    if (!formData.difficultyLevel)      e.difficultyLevel = "Difficulty level is required";
    if (!formData.questionType)         e.questionType   = "Question type is required";
    if (isHtmlEmpty(formData.question) && !formData.questionAttachment)
      e.question = "Question text or image is required";
    if (dupError) e.question = dupError;
    if (isMCQ) {
      if (correctIds.length === 0)      e.answer         = "Please mark at least one correct option";
    } else {
      if (isHtmlEmpty(formData.answer) && !formData.answerAttachment)
        e.answer = "Answer text or image is required";
    }
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ve = validate();
    if (Object.keys(ve).length > 0) {
      setErrors(ve);
      setErrorMessage("Please correct the errors below.");
      return;
    }

    if (isMCQ) {
      try {
        const optRes = await fetch(`${API_URL}/api/options/${questionId}`);
        const optData = await optRes.json();
        const options = optData.options || [];
        if (options.length === 0) {
          setErrorMessage("Please add at least one option for updating question.");
          return;
        }
        if (options.some((o) => !o.option || !o.option.trim())) {
          setErrorMessage("Please remove blank options before updating.");
          return;
        }
      } catch {
        setErrorMessage("Error checking options. Try again.");
        return;
      }
    }

    setLoading(true);
    try {
      const cleanData = {
        ...formData,
        question: stripUploads(formData.question),
        answerExplanation: stripUploads(formData.answerExplanation),
        answer: isMCQ ? correctIds.join(",") : stripUploads(formData.answer),
        labels: formData.labels.map((l) => l.value),
        alternateAnswers: formData.alternateAnswers.map((a) => a.trim()).filter(Boolean),
        rejectedAnswers: formData.rejectedAnswers.map((a) => a.trim()).filter(Boolean),
        // Only a code question has test cases; switching type away drops them.
        testCases: formData.questionType === CODE_QUESTION_TYPE ? cleanTestCases(formData.testCases) : [],
        starterCode: formData.questionType === CODE_QUESTION_TYPE ? formData.starterCode : "",
      };
      const payload = cleanData;
      const response = await fetch(`${API_URL}/api/questions/${questionId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || "Failed to update question");
      }
      if (embedded) onSaved?.();
      else navigate("/questions", { state: { successMessage: "Question updated successfully!" } });
    } catch (err) {
      setErrorMessage(err.message || "Update failed.");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      const optRes = await fetch(`${API_URL}/api/options/${questionId}`);
      if (!optRes.ok) throw new Error("Failed to fetch options");
      const optData = await optRes.json();
      for (const opt of optData.options || []) {
        const delRes = await fetch(`${API_URL}/api/options/${opt._id}`, { method: "DELETE" });
        if (!delRes.ok) throw new Error(`Failed to delete option ${opt._id}`);
      }
      const qDelRes = await fetch(`${API_URL}/api/questions/${questionId}`, { method: "DELETE" });
      if (!qDelRes.ok) throw new Error("Failed to delete question");
      if (embedded) onDeleted?.();
      else navigate("/questions", { state: { successMessage: "Question deleted successfully!" } });
    } catch (error) {
      console.error("Error deleting question:", error);
      alert("Error deleting question: " + error.message);
    }
  };

  return (
    <div className={`af-page qf-page${embedded ? " qf-embedded" : ""}`} ref={pageRef}>
      {loading && (
        <div className="af-overlay">
          <div className="af-spinner" />
          <p className="af-overlay-text">Saving changes…</p>
        </div>
      )}

      {showDeletePopup && (
        <div className="uf-delete-overlay">
          <div className="uf-delete-modal">
            <p className="uf-delete-msg">Are you sure you want to delete this question?</p>
            <div className="uf-delete-actions">
              <button className="uf-cancel-btn" onClick={() => setShowDeletePopup(false)}>Cancel</button>
              <button className="uf-confirm-btn" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="af-card qf-card">
        {!embedded && (
          <div className="qf-head">
            <div className="qf-head-text">
              <h2 className="qf-title">Edit question</h2>
              <p className="qf-subtitle">Changes apply everywhere this question is used.</p>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="af-alert af-alert-error" style={{ margin: "16px 32px 0" }}>
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="af-body qf-layout">
            <div className="qf-main">
            <div className="af-section">
              <p className="af-section-title">Question</p>
              <Field label="Question Text" required error={errors.question}>
                <div style={errors.question || dupError ? errorBorder : {}}>
                  <RichTextEditor
                    value={formData.question}
                    onChange={(html) => {
                      setFormData(p => ({ ...p, question: html }));
                      clearError("question");
                      setDupError("");
                    }}
                    onBlur={(html) => checkDuplicate(html)}
                    placeholder="Type the full question here… (paste images directly)"
                    toolbar="full"
                    height={180}
                  />
                </div>
                {dupChecking && (
                  <span style={{ fontSize: 12, color: "#6b7280", marginTop: 4, display: "block" }}>
                    Checking for duplicates…
                  </span>
                )}
                {dupError && !errors.question && (
                  <span className="af-error">{dupError}</span>
                )}
              </Field>
              <Field label="Question Image (optional)" error={errors.questionAttachment}>
                <ImageUploadField
                  value={formData.questionAttachment}
                  onChange={(url) => setFormData((p) => ({ ...p, questionAttachment: url }))}
                />
              </Field>

              {isMCQ && (
                <div className="af-options-block">
                  <div className="af-action-strip">
                    <button
                      type="button"
                      className="af-secondary-btn"
                      onClick={() => setShowOptionDialog(true)}
                    >
                      + Add Options
                    </button>
                  </div>
                  <UpdateOptionsTable
                    questionId={questionId}
                    openDialogExternally={showOptionDialog}
                    setOpenDialogExternally={setShowOptionDialog}
                    handleOptionsChange={() => setErrorMessage("")}
                    correctIds={correctIds}
                    onCorrectChange={(ids) => {
                      setCorrectIds(ids);
                      clearError("answer");
                    }}
                    onOptionsLoaded={(opts) => setLoadedOptions(opts)}
                  />
                  {errors.answer && <span className="af-error">{errors.answer}</span>}
                </div>
              )}
            </div>

            <div className="af-section">
              <p className="af-section-title">Answer</p>
              {!isMCQ && (
                <Field label="Answer" required error={errors.answer}>
                  <div style={errors.answer ? errorBorder : {}}>
                    <RichTextEditor
                      value={formData.answer}
                      onChange={(html) => { setFormData(p => ({ ...p, answer: html })); clearError("answer"); }}
                      placeholder="Correct answer… (paste images directly)"
                      toolbar="full"
                      height={150}
                    />
                  </div>
                </Field>
              )}
              {!isMCQ && (
                <Field label="Alternate Answers (optional)">
                  <AlternateAnswersField
                    value={formData.alternateAnswers}
                    onChange={(alternateAnswers) => setFormData((p) => ({ ...p, alternateAnswers }))}
                  />
                </Field>
              )}
              {/* Learned from trainers' verdicts; the Buddy app marks these
                  incorrect automatically. Remove one to undo a wrong verdict. */}
              {!isMCQ && formData.rejectedAnswers.length > 0 && (
                <Field label="Answers marked incorrect">
                  <AlternateAnswersField
                    value={formData.rejectedAnswers}
                    onChange={(rejectedAnswers) => setFormData((p) => ({ ...p, rejectedAnswers }))}
                    placeholder="An answer that is graded incorrect…"
                    addLabel="+ Add incorrect answer"
                  />
                </Field>
              )}
              {formData.questionType === CODE_QUESTION_TYPE && (
                <Field label="Test cases (optional)">
                  <TestCasesField
                    value={formData.testCases}
                    onChange={(testCases) => setFormData((p) => ({ ...p, testCases }))}
                  />
                </Field>
              )}
              {formData.questionType === CODE_QUESTION_TYPE && (
                <Field label="Starter code (optional)">
                  <StarterCodeField
                    value={formData.starterCode}
                    onChange={(starterCode) => setFormData((p) => ({ ...p, starterCode }))}
                  />
                </Field>
              )}
              {isMCQ && (() => {
                const correctOpts = loadedOptions.filter((o) =>
                  correctIds.includes(o._id.toString())
                );
                return correctOpts.length === 0 ? (
                  <p className="af-mcq-answer-hint">
                    No correct option selected yet — use the checkboxes in the Options section above.
                  </p>
                ) : (
                  <div className="af-mcq-correct-preview">
                    <p className="af-mcq-correct-label">
                      Correct answer{correctOpts.length > 1 ? "s" : ""}:
                    </p>
                    {correctOpts.map((o) => (
                      <div key={o._id} className="af-mcq-correct-opt">
                        {o.option && (
                          <span
                            className="af-mcq-correct-opt-text"
                            dangerouslySetInnerHTML={{ __html: prefixUploads(o.option) }}
                          />
                        )}
                        {o.optionAttachment && (
                          <img
                            src={
                              o.optionAttachment.startsWith("/uploads/")
                                ? `${API_URL}${o.optionAttachment}`
                                : o.optionAttachment
                            }
                            alt="option"
                            className="af-mcq-correct-opt-img"
                          />
                        )}
                        {!o.option && !o.optionAttachment && (
                          <span className="af-mcq-correct-opt-empty">(empty option)</span>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
              <Field label="Answer Image (optional)" error={errors.answerAttachment}>
                <ImageUploadField
                  value={formData.answerAttachment}
                  onChange={(url) => setFormData((p) => ({ ...p, answerAttachment: url }))}
                />
              </Field>
              <Field label="Explanation" error={errors.answerExplanation}>
                <RichTextEditor
                  value={formData.answerExplanation}
                  onChange={(html) => setFormData(p => ({ ...p, answerExplanation: html }))}
                  placeholder="Why is this the correct answer… (paste images directly)"
                  toolbar="full"
                  height={200}
                />
              </Field>
            </div>
            </div>
            <aside className="qf-side">
            <div className="af-section">
              <p className="af-section-title">Classification</p>
              <div className="af-row">
                <Field label="Subject" required error={errors.subject}>
                  <HierarchyDropdown
                    options={subjects}
                    value={formData.subject}
                    onChange={handleSubjectChange}
                    placeholder="Select subject…"
                    hasError={!!errors.subject}
                  />
                </Field>
                <Field label="Module" error={errors.module}>
                  <HierarchyDropdown
                    options={modules}
                    value={formData.module}
                    onChange={handleModuleChange}
                    placeholder={formData.subject ? "Select module…" : "Select a subject first"}
                    isDisabled={!formData.subject}
                    isLoading={loadingModules}
                    hasError={!!errors.module}
                  />
                </Field>
              </div>
              <div className="af-row">
                <Field label="Topic" error={errors.topic}>
                  <HierarchyDropdown
                    options={topics}
                    value={formData.topic}
                    onChange={handleTopicChange}
                    placeholder={formData.module ? "Select topic…" : "Select a module first"}
                    isDisabled={!formData.module}
                    isLoading={loadingTopics}
                    hasError={!!errors.topic}
                  />
                </Field>
                <Field label="Section" error={errors.section}>
                  <HierarchyDropdown
                    options={sections}
                    value={formData.section}
                    onChange={handleSectionChange}
                    placeholder={formData.topic ? "Select or type a section…" : "Select a topic first"}
                    isDisabled={!formData.topic}
                    isLoading={loadingSections}
                    hasError={!!errors.section}
                    isCreatable
                  />
                </Field>
              </div>
              <div className="af-row">
                <Field label="Labels">
                  <MultiSelectDropdown
                    options={availableLabels}
                    value={formData.labels}
                    onChange={(val) => setFormData((p) => ({ ...p, labels: val }))}
                    placeholder="Add labels…"
                    isCreatable
                    onCreateOption={handleCreateLabel}
                  />
                </Field>
              </div>
              <div className="af-row">
                <Field label="Difficulty Level" required error={errors.difficultyLevel}>
                  <select
                    name="difficultyLevel"
                    value={formData.difficultyLevel}
                    onChange={handleChange}
                    className={errors.difficultyLevel ? "error-input" : ""}
                  >
                    <option value="">Select difficulty…</option>
                    {DIFFICULTY.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </Field>
                <Field label="Question Type" required error={errors.questionType}>
                  <select
                    name="questionType"
                    value={formData.questionType}
                    onChange={handleChange}
                    className={errors.questionType ? "error-input" : ""}
                  >
                    <option value="">Select type…</option>
                    {QUESTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
            </div>

            <div className="af-section">
              <p className="af-section-title">Additional Details</p>
              <div className="af-row">
                <Field label="Notes" error={errors.notes}>
                  <input
                    type="text"
                    name="notes"
                    placeholder="Any extra notes…"
                    value={formData.notes}
                    onChange={handleChange}
                  />
                </Field>
                <Field label="Reference Link" error={errors.questionLink}>
                  <input
                    type="text"
                    name="questionLink"
                    placeholder="External resource URL"
                    value={formData.questionLink}
                    onChange={handleChange}
                  />
                </Field>
              </div>
              <div className="af-row">
                <Field label="Positive Marks" error={errors.markPositive}>
                  <input
                    type="number"
                    name="markPositive"
                    placeholder="e.g. 4"
                    value={formData.markPositive}
                    onChange={handleChange}
                  />
                </Field>
                <Field label="Negative Marks" error={errors.markNegative}>
                  <input
                    type="number"
                    name="markNegative"
                    placeholder="e.g. -1"
                    value={formData.markNegative}
                    onChange={handleChange}
                  />
                </Field>
              </div>
            </div>
            </aside>
          </div>

          <div className="qf-footer">
            <button type="button" className="qf-btn qf-btn-danger" onClick={() => setShowDeletePopup(true)}>Delete</button>
            <div className="qf-footer-actions">
              <button type="button" className="qf-btn qf-btn-ghost"
                onClick={() => (embedded ? onCancel?.() : navigate(`/questions/${questionId}`))}>Cancel</button>
              <button type="submit" className="qf-btn qf-btn-primary" disabled={loading}>
                {loading ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateQuestion;
